/**
 * Profile authoring kit.
 *
 * The database has 300+ entries and must scale to 10,000. Authoring each one as
 * a hand-written 300-line literal would be unmaintainable and would invite
 * copy-paste errors, so instead each entry supplies the facts that actually
 * distinguish it -- architecture, converter core, output stage, published
 * distortion/noise specs, output voltage, filter set -- and these builders
 * expand those facts into a complete multidimensional profile using documented
 * physical rules.
 *
 * IMPORTANT: the expansion is a *derivation*, and it is tagged as such. Values
 * that came from a manufacturer datasheet keep MANUFACTURER_SPEC; values this
 * file computes are DERIVED; values chosen from topology norms are ESTIMATED or
 * MODELLED. Nothing here is ever labelled MEASURED (spec 8/51).
 */

import type {
  AmpClippingModel,
  AmpDynamicsModel,
  AmpForm,
  AmpOutputModel,
  AmpPowerSpec,
  AmpProfile,
  AmpTopology,
  AnalogOutputModel,
  CabinetModel,
  CrossoverSection,
  DacArchitecture,
  DacConversionModel,
  DacOutputStage,
  DacProfile,
  DigitalFilterOption,
  DirectivityModel,
  DriverTech,
  EnclosureType,
  HeadphoneProfile,
  NonlinearModel,
  PriceInfo,
  PriceStatus,
  ProfileImage,
  SpeakerDistortionModel,
  SpeakerProfile,
  SpeakerType,
} from '../types/hardware';
import type {
  ChannelBehaviour,
  FRCurve,
  FRPoint,
  HarmonicSpectrum,
  ImpedanceCurve,
  NoiseProfile,
  ZPoint,
} from '../types/curves';
import { frCurve, impedanceCurve } from '../types/curves';
import type { Confidence } from '../types/provenance';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

export const usd = (
  amount: number,
  status: PriceStatus = 'MSRP',
  year?: number,
  note?: string,
): PriceInfo => ({ amount, currency: 'USD', status, year, note });

export const eur = (amount: number, status: PriceStatus = 'MSRP', year?: number): PriceInfo => ({
  amount, currency: 'EUR', status, year,
});

export const slug = (mfr: string, model: string, cat: string) =>
  `${cat}.${mfr.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.${model
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

export const img = (seed: string, accent?: string): ProfileImage => ({
  kind: 'generated',
  seed,
  accent,
});

/** dB <-> linear. */
export const dbToLin = (db: number) => Math.pow(10, db / 20);
export const linToDb = (x: number) => 20 * Math.log10(Math.max(1e-12, x));

/**
 * Derive a harmonic spectrum from a published THD figure and a topology-typical
 * harmonic *distribution*. THD alone does not tell you what the distortion
 * sounds like; the distribution is what differentiates a 300B from a Class-D
 * module, so each topology declares its own decay law and even/odd split.
 */
export interface HarmonicShape {
  /** Relative weight of each harmonic order 2..9, before normalisation. */
  weights: number[];
  /** Growth of each order in dB per dB of input drive. */
  growth?: number[];
  asymmetry?: number;
}

export const HARMONIC_SHAPES: Record<string, HarmonicShape> = {
  // Single-ended triode: dominant H2, fast decay, strongly asymmetric.
  set: { weights: [1.0, 0.22, 0.06, 0.02, 0.008, 0.003, 0.0012, 0.0005], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.85 },
  // Push-pull tube: H2 partially cancelled, H3 dominant, moderate decay.
  pushPullTube: { weights: [0.45, 1.0, 0.16, 0.09, 0.03, 0.012, 0.005, 0.002], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.35 },
  // OTL: no transformer, higher H3 with load dependence.
  otl: { weights: [0.55, 1.0, 0.22, 0.11, 0.05, 0.02, 0.008, 0.003], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.4 },
  // Class A solid state: low order, H2/H3 similar, very fast decay.
  classA: { weights: [1.0, 0.5, 0.07, 0.03, 0.008, 0.003, 0.001, 0.0004], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.45 },
  // Class AB: crossover residue lifts higher odd orders.
  classAB: { weights: [0.7, 1.0, 0.28, 0.22, 0.10, 0.06, 0.03, 0.015], growth: [1, 2, 2.4, 2.6, 2.8, 3, 3.1, 3.2], asymmetry: 0.3 },
  // Class D: low LF distortion, HF residue, odd-dominant.
  classD: { weights: [0.5, 1.0, 0.30, 0.26, 0.14, 0.10, 0.06, 0.04], growth: [1, 2, 2.2, 2.4, 2.6, 2.8, 3, 3.1], asymmetry: 0.2 },
  // High-feedback solid state: extremely low, but what remains is high order.
  highFeedback: { weights: [0.6, 1.0, 0.4, 0.35, 0.25, 0.2, 0.15, 0.12], growth: [1, 2, 2.5, 3, 3.5, 4, 4.5, 5], asymmetry: 0.15 },
  // Delta-sigma DAC output stage: dominated by op-amp, very low, odd-ish.
  deltaSigma: { weights: [0.8, 1.0, 0.15, 0.10, 0.04, 0.02, 0.01, 0.005], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.25 },
  // R-2R ladder: level-dependent INL creates a broad, slowly decaying tail.
  r2r: { weights: [1.0, 0.75, 0.35, 0.25, 0.15, 0.10, 0.07, 0.05], growth: [0.6, 1.2, 1.6, 2.0, 2.2, 2.4, 2.6, 2.8], asymmetry: 0.5 },
  // Tube-output DAC: SET-like but at line level.
  tubeDac: { weights: [1.0, 0.28, 0.08, 0.03, 0.012, 0.005, 0.002, 0.001], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.75 },
  // Transformer-coupled: H2 + core hysteresis lifting low orders at LF.
  transformer: { weights: [1.0, 0.42, 0.18, 0.09, 0.04, 0.02, 0.009, 0.004], growth: [1, 1.8, 2.4, 3, 3.4, 3.8, 4.2, 4.6], asymmetry: 0.6 },
  // Loudspeaker: H2/H3 dominant, strong level and frequency dependence.
  speaker: { weights: [1.0, 0.65, 0.18, 0.10, 0.05, 0.03, 0.015, 0.008], growth: [1, 2, 2.5, 3, 3.2, 3.4, 3.6, 3.8], asymmetry: 0.5 },
  // Electrostatic: exceptionally linear, mostly odd from field non-uniformity.
  electrostatic: { weights: [0.25, 1.0, 0.10, 0.06, 0.02, 0.01, 0.004, 0.002], growth: [1, 2, 3, 4, 5, 6, 7, 8], asymmetry: 0.1 },
  // Planar magnetic: low, even-dominant at LF from suspension asymmetry.
  planar: { weights: [1.0, 0.45, 0.12, 0.07, 0.03, 0.015, 0.006, 0.003], growth: [1, 2, 2.8, 3.4, 3.8, 4.2, 4.6, 5], asymmetry: 0.4 },
  // Compression driver / horn: H2 rises with SPL from air nonlinearity.
  horn: { weights: [1.0, 0.5, 0.22, 0.12, 0.06, 0.03, 0.018, 0.01], growth: [1.2, 2.2, 2.8, 3.2, 3.6, 4, 4.4, 4.8], asymmetry: 0.55 },
};

/**
 * Build a HarmonicSpectrum whose *total* harmonic distortion equals `thdPercent`
 * at `refLevelDbFS`, distributed according to `shape`.
 */
export function harmonicsFromThd(
  thdPercent: number,
  shape: HarmonicShape,
  refLevelDbFS = -6,
  confidence: Confidence = 'DERIVED',
  source?: string,
): HarmonicSpectrum {
  const w = shape.weights;
  const norm = Math.sqrt(w.reduce((s, x) => s + x * x, 0)) || 1;
  const total = thdPercent / 100;
  const orders: Record<number, number> = {};
  const growthDbPerDb: Record<number, number> = {};
  for (let i = 0; i < w.length; i++) {
    const order = i + 2;
    const amp = (w[i] / norm) * total;
    orders[order] = amp > 1e-9 ? linToDb(amp) : -200;
    growthDbPerDb[order] = shape.growth?.[i] ?? order - 1;
  }
  return {
    kind: 'harmonics',
    refLevelDbFS,
    orders,
    growthDbPerDb,
    asymmetry: shape.asymmetry ?? 0.3,
    confidence,
    source,
  };
}

export function noiseFrom(
  dynamicRangeDb: number,
  shape: NoiseProfile['shape'] = 'thermal',
  extra: Partial<NoiseProfile> = {},
  confidence: Confidence = 'DERIVED',
): NoiseProfile {
  return {
    kind: 'noise',
    levelDbFS: -Math.abs(dynamicRangeDb),
    shape,
    channelCorrelation: 0.05,
    confidence,
    ...extra,
  };
}

export function channels(
  crosstalkDb: number,
  imbalanceDb = 0.05,
  extra: Partial<ChannelBehaviour> = {},
): ChannelBehaviour {
  return {
    crosstalkDb,
    imbalanceDb,
    crosstalkSlopeDbDec: 18,
    skewSamples: 0,
    ...extra,
  };
}

// ---------------------------------------------------------------------------
// Reconstruction filter library
// ---------------------------------------------------------------------------

export const FILTERS = {
  sharpLinear: (taps = 128): DigitalFilterOption => ({
    id: 'sharp-linear', label: 'Sharp roll-off, linear phase', mode: 'linear-phase',
    cutoffRatio: 0.905, transitionRatio: 0.09, stopbandDb: -120, taps, confidence: 'MODELLED',
  }),
  sharpMinimum: (taps = 96): DigitalFilterOption => ({
    id: 'sharp-min', label: 'Sharp roll-off, minimum phase', mode: 'minimum-phase',
    cutoffRatio: 0.905, transitionRatio: 0.09, stopbandDb: -120, taps, confidence: 'MODELLED',
  }),
  slowLinear: (taps = 48): DigitalFilterOption => ({
    id: 'slow-linear', label: 'Slow roll-off, linear phase', mode: 'slow-linear',
    cutoffRatio: 0.72, transitionRatio: 0.42, stopbandDb: -55, taps, confidence: 'MODELLED',
  }),
  slowMinimum: (taps = 32): DigitalFilterOption => ({
    id: 'slow-min', label: 'Slow roll-off, minimum phase', mode: 'slow-minimum',
    cutoffRatio: 0.72, transitionRatio: 0.42, stopbandDb: -55, taps, confidence: 'MODELLED',
  }),
  apodizing: (taps = 128): DigitalFilterOption => ({
    id: 'apodizing', label: 'Apodizing (no pre-ringing)', mode: 'apodizing',
    cutoffRatio: 0.86, transitionRatio: 0.16, stopbandDb: -100, taps, confidence: 'MODELLED',
  }),
  hybrid: (taps = 96): DigitalFilterOption => ({
    id: 'hybrid', label: 'Hybrid phase', mode: 'hybrid',
    cutoffRatio: 0.88, transitionRatio: 0.14, stopbandDb: -95, taps, confidence: 'MODELLED',
  }),
  brickwall: (taps = 1024): DigitalFilterOption => ({
    id: 'brickwall', label: 'Brick-wall (long tap WTA)', mode: 'brickwall',
    cutoffRatio: 0.94, transitionRatio: 0.05, stopbandDb: -150, taps, confidence: 'MODELLED',
  }),
  nos: (): DigitalFilterOption => ({
    id: 'nos', label: 'Non-oversampling (zero-order hold)', mode: 'nos',
    nosHoldOrder: 1, confidence: 'MODELLED',
  }),
  nosCompensated: (): DigitalFilterOption => ({
    id: 'nos-comp', label: 'NOS with sinc droop compensation', mode: 'nos',
    nosHoldOrder: 2, confidence: 'MODELLED',
  }),
};

/** The filter set most delta-sigma chipsets expose. */
export const ESS_FILTERS = (): DigitalFilterOption[] => [
  FILTERS.sharpLinear(128),
  FILTERS.slowLinear(48),
  FILTERS.sharpMinimum(96),
  FILTERS.slowMinimum(32),
  FILTERS.apodizing(128),
  FILTERS.hybrid(96),
];

export const AKM_FILTERS = (): DigitalFilterOption[] => [
  FILTERS.sharpLinear(128),
  FILTERS.slowLinear(48),
  FILTERS.sharpMinimum(96),
  FILTERS.slowMinimum(32),
  { ...FILTERS.slowLinear(24), id: 'super-slow', label: 'Super slow roll-off', cutoffRatio: 0.55, transitionRatio: 0.6, stopbandDb: -32 },
  FILTERS.hybrid(96),
];

export const SIMPLE_FILTERS = (): DigitalFilterOption[] => [
  FILTERS.sharpLinear(128),
  FILTERS.sharpMinimum(96),
];

// ---------------------------------------------------------------------------
// Response shape library -- authored as sparse breakpoints
// ---------------------------------------------------------------------------

/** Essentially ruler-flat line-level device. */
export const FLAT_LINE = (hf = 0): FRCurve =>
  frCurve([[10, 0], [20, 0], [1000, 0], [10000, 0], [20000, hf], [40000, hf * 3 - 0.4], [80000, hf * 6 - 3]], 'MANUFACTURER_SPEC');

/** Transformer-coupled line stage: gentle LF and HF rolloff. */
export const TRANSFORMER_LINE = (lf = 12, hf = 45000): FRCurve =>
  frCurve(
    [
      [5, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 5, 2)))],
      [10, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 10, 2)))],
      [20, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 20, 2)))],
      [50, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 50, 2)))],
      [1000, 0],
      [10000, 0],
      [20000, -20 * Math.log10(Math.sqrt(1 + Math.pow(20000 / hf, 2)))],
      [40000, -20 * Math.log10(Math.sqrt(1 + Math.pow(40000 / hf, 2)))],
    ],
    'DERIVED',
    'First-order high-pass/low-pass derived from quoted -3 dB points',
  );

/** Tube output stage: mild HF softening and LF coupling-cap rolloff. */
export const TUBE_LINE = (lf = 8, hfTiltDb = -0.6): FRCurve =>
  frCurve(
    [
      [5, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 5, 2)))],
      [20, -20 * Math.log10(Math.sqrt(1 + Math.pow(lf / 20, 2)))],
      [100, 0], [1000, 0], [5000, hfTiltDb * 0.25],
      [10000, hfTiltDb * 0.5], [20000, hfTiltDb], [40000, hfTiltDb * 3],
    ],
    'MODELLED',
  );

/**
 * NOS DAC without droop compensation: the zero-order hold imposes a sinc
 * envelope, -3.92 dB at Nyquist. This is a computed physical consequence, so it
 * is DERIVED rather than invented.
 */
export const NOS_DROOP = (fs = 44100): FRCurve => {
  const pts: FRPoint[] = [];
  for (const f of [20, 100, 1000, 2000, 5000, 8000, 10000, 12000, 15000, 17000, 19000, 20000]) {
    const x = (Math.PI * f) / fs;
    const sinc = x === 0 ? 1 : Math.sin(x) / x;
    pts.push([f, linToDb(sinc)]);
  }
  return { kind: 'fr', points: pts, confidence: 'DERIVED', source: 'sin(x)/x zero-order-hold envelope' };
};

// ---------------------------------------------------------------------------
// DAC builder
// ---------------------------------------------------------------------------

export interface DacSpecInput {
  mfr: string;
  model: string;
  price: PriceInfo;
  year?: number;
  country?: string;
  architecture: DacArchitecture;
  core: string;
  outputStage: DacOutputStage;
  /** Published dynamic range / SNR in dB (A-weighted). */
  dynamicRangeDb: number;
  /** Published THD+N in dB (negative), e.g. -120. */
  thdPlusNDb: number;
  /** Crosstalk / channel separation in dB (negative). */
  crosstalkDb?: number;
  outputVrmsSE?: number;
  outputVrmsBal?: number;
  outputImpedanceOhms?: number;
  balanced?: boolean;
  sampleRates?: number[];
  maxBitDepth?: number;
  dsd?: DacProfile['dsdSupport'];
  filters?: DigitalFilterOption[];
  defaultFilterId?: string;
  effectiveBits?: number;
  jitterPsRms?: number;
  /** Override the harmonic distribution; defaults from architecture+stage. */
  harmonicShape?: HarmonicShape;
  /** Analog response override. */
  response?: FRCurve;
  nonlinear?: Partial<NonlinearModel>;
  notes?: string;
  tags?: string[];
  confidence?: Confidence;
  provenanceNote?: string;
  accent?: string;
  ladderMismatchLsb?: number;
  noiseShaperOrder?: number;
  idleToneLevel?: number;
  internalOversampling?: number;
  parallelUnits?: number;
  slewRateVus?: number;
  version?: number;
}

function dacHarmonicShape(i: DacSpecInput): HarmonicShape {
  if (i.harmonicShape) return i.harmonicShape;
  if (i.outputStage === 'tube') return HARMONIC_SHAPES.tubeDac;
  if (i.outputStage === 'transformer') return HARMONIC_SHAPES.transformer;
  if (i.architecture === 'r2r' || i.architecture === 'discrete-r2r' || i.architecture === 'ladder-multibit')
    return HARMONIC_SHAPES.r2r;
  if (i.outputStage === 'discrete-class-a' || i.outputStage === 'jfet') return HARMONIC_SHAPES.classA;
  return HARMONIC_SHAPES.deltaSigma;
}

function dacNonlinear(i: DacSpecInput): NonlinearModel {
  const base: NonlinearModel = {
    kind: 'tanh',
    headroomDb: 14,
    amount: 0.35,
    bias: 0,
    asymmetry: 0.1,
    oversample: 4,
    confidence: 'MODELLED',
  };
  const isLadder =
    i.architecture === 'r2r' || i.architecture === 'discrete-r2r' || i.architecture === 'ladder-multibit';
  if (isLadder) {
    Object.assign(base, {
      kind: 'r2r-inl' as const,
      inlLsb: i.ladderMismatchLsb ?? 0.4,
      amount: 0.6,
      asymmetry: 0.3,
      headroomDb: 6,
    });
  } else if (i.outputStage === 'tube') {
    Object.assign(base, { kind: 'triode' as const, amount: 0.55, asymmetry: 0.6, bias: 0.7, headroomDb: 16 });
  } else if (i.outputStage === 'transformer') {
    Object.assign(base, { kind: 'transformer-core' as const, coreKnee: 0.55, hysteresis: 0.22, amount: 0.5, headroomDb: 12 });
  } else if (i.outputStage === 'discrete-class-a' || i.outputStage === 'jfet') {
    Object.assign(base, { kind: 'jfet-square' as const, amount: 0.4, asymmetry: 0.35, bias: 0.5 });
  } else if (i.architecture === 'delta-sigma' || i.architecture === 'multibit-delta-sigma') {
    Object.assign(base, { kind: 'sigma-delta' as const, amount: 0.3, asymmetry: 0.08 });
  }
  return { ...base, ...i.nonlinear } as NonlinearModel;
}

export function makeDac(i: DacSpecInput): DacProfile {
  const id = slug(i.mfr, i.model, 'dac');
  const filters = i.filters ?? (i.architecture === 'r2r' || i.architecture === 'discrete-r2r'
    ? [FILTERS.nos(), FILTERS.sharpLinear(128), FILTERS.sharpMinimum(96)]
    : ESS_FILTERS());
  const thdPercent = Math.pow(10, i.thdPlusNDb / 20) * 100;
  const isNos = filters[0]?.mode === 'nos';
  const shape = dacHarmonicShape(i);

  const analog: AnalogOutputModel = {
    stage: i.outputStage,
    outputVrmsSE: i.outputVrmsSE ?? 2.0,
    outputVrmsBal: i.outputVrmsBal ?? (i.balanced ? 4.0 : undefined),
    outputImpedanceOhms: i.outputImpedanceOhms ?? (i.outputStage === 'tube' ? 600 : 50),
    balancedAvailable: i.balanced ?? true,
    response:
      i.response ??
      (i.outputStage === 'tube'
        ? TUBE_LINE(8, -0.8)
        : i.outputStage === 'transformer'
          ? TRANSFORMER_LINE(10, 42000)
          : FLAT_LINE(-0.05)),
    slewRateVus: i.slewRateVus,
    confidence: i.response ? (i.response.confidence ?? 'MODELLED') : 'MODELLED',
  };

  const conversion: DacConversionModel = {
    architecture: i.architecture,
    core: i.core,
    effectiveBits: i.effectiveBits ?? Math.min(24, Math.round((i.dynamicRangeDb - 1.76) / 6.02)),
    parallelUnits: i.parallelUnits,
    internalOversampling: i.internalOversampling ?? (isNos ? 1 : 8),
    ladderMismatchLsb: i.ladderMismatchLsb,
    idleToneLevel: i.idleToneLevel,
    noiseShaperOrder: i.noiseShaperOrder,
    jitterPsRms: i.jitterPsRms ?? 60,
    jitterKind: 'random',
    confidence: 'DERIVED',
  };

  return {
    id,
    category: 'dac',
    manufacturer: i.mfr,
    model: i.model,
    price: i.price,
    version: i.version ?? 1,
    revised: '2026-08-22',
    confidence: i.confidence ?? 'ESTIMATED',
    provenanceNote:
      i.provenanceNote ??
      'Electrical specifications from published manufacturer data; DSP behaviour derived from those specs plus architecture-typical distortion structure. Not a measurement of an individual unit.',
    notes: i.notes,
    tags: [...(i.tags ?? []), i.architecture, i.outputStage],
    year: i.year,
    countryOfOrigin: i.country,
    image: img(id, i.accent),
    conversion,
    filters,
    defaultFilterId: i.defaultFilterId ?? filters[0].id,
    analog,
    nonlinear: dacNonlinear(i),
    harmonics: harmonicsFromThd(thdPercent, shape, -6, 'DERIVED', `Distributed from published THD+N ${i.thdPlusNDb} dB`),
    noise: noiseFrom(i.dynamicRangeDb, i.architecture.includes('delta-sigma') ? 'shaped' : 'thermal', {
      shapingSlopeDbOct: i.architecture.includes('delta-sigma') ? 9 : 0,
      shapeKneeHz: 22000,
      channelCorrelation: 0.05,
    }),
    channels: channels(i.crosstalkDb ?? -120, 0.04),
    sampleRates: i.sampleRates ?? [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: i.maxBitDepth ?? 32,
    dsdSupport: i.dsd ?? 'dsd256',
    dynamicRangeDb: i.dynamicRangeDb,
    thdPlusNDb: i.thdPlusNDb,
    rateDependentResponse: isNos
      ? { '44100': NOS_DROOP(44100), '48000': NOS_DROOP(48000), '96000': NOS_DROOP(96000), '192000': NOS_DROOP(192000) }
      : undefined,
    time: {
      preRing: filters[0].mode === 'linear-phase' || filters[0].mode === 'brickwall' ? 1 : filters[0].mode === 'hybrid' ? 0.4 : 0.05,
      postRing: filters[0].mode === 'nos' ? 0.1 : 1,
    },
  };
}

// ---------------------------------------------------------------------------
// Amplifier builder
// ---------------------------------------------------------------------------

export interface AmpSpecInput {
  mfr: string;
  model: string;
  price: PriceInfo;
  year?: number;
  country?: string;
  topology: AmpTopology;
  form: AmpForm;
  devices?: string;
  power: AmpPowerSpec;
  gainDb?: number;
  /** Published THD at rated power, percent. */
  thdPercent: number;
  /** Signal-to-noise ratio, dB. */
  snrDb: number;
  outputImpedanceOhms?: number;
  dampingFactor?: number;
  feedbackDb?: number;
  outputTransformer?: boolean;
  transformerLfHz?: number;
  transformerHfHz?: number;
  /** -3 dB bandwidth [low, high] in Hz. */
  bandwidth?: [number, number];
  response?: FRCurve;
  crosstalkDb?: number;
  harmonicShape?: HarmonicShape;
  nonlinear?: Partial<NonlinearModel>;
  dynamics?: Partial<AmpDynamicsModel>;
  clipping?: Partial<AmpClippingModel>;
  balancedInput?: boolean;
  electrostaticDrive?: boolean;
  estatBias?: AmpProfile['estatBias'];
  notes?: string;
  tags?: string[];
  confidence?: Confidence;
  provenanceNote?: string;
  accent?: string;
  version?: number;
}

const AMP_SHAPE_BY_TOPOLOGY: Record<AmpTopology, HarmonicShape> = {
  'solid-state-ab': HARMONIC_SHAPES.classAB,
  'solid-state-a': HARMONIC_SHAPES.classA,
  'class-d': HARMONIC_SHAPES.classD,
  'class-g': HARMONIC_SHAPES.classAB,
  'class-h': HARMONIC_SHAPES.classAB,
  'tube-push-pull': HARMONIC_SHAPES.pushPullTube,
  'tube-set': HARMONIC_SHAPES.set,
  'tube-otl': HARMONIC_SHAPES.otl,
  hybrid: HARMONIC_SHAPES.classA,
  'transformer-coupled': HARMONIC_SHAPES.transformer,
};

const AMP_NL_BY_TOPOLOGY: Record<AmpTopology, NonlinearModel> = {
  'solid-state-ab': { kind: 'class-b-crossover', amount: 0.45, crossoverWidth: 0.004, headroomDb: 10, asymmetry: 0.15, oversample: 4 },
  'solid-state-a': { kind: 'jfet-square', amount: 0.4, asymmetry: 0.4, bias: 0.55, headroomDb: 12, oversample: 4 },
  'class-d': { kind: 'class-d-pwm', amount: 0.4, carrierHz: 450000, headroomDb: 8, asymmetry: 0.1, oversample: 4 },
  'class-g': { kind: 'class-b-crossover', amount: 0.5, crossoverWidth: 0.006, headroomDb: 10, asymmetry: 0.2, oversample: 4 },
  'class-h': { kind: 'class-b-crossover', amount: 0.5, crossoverWidth: 0.005, headroomDb: 10, asymmetry: 0.2, oversample: 4 },
  'tube-push-pull': { kind: 'pentode', amount: 0.6, asymmetry: 0.3, bias: 0.25, headroomDb: 10, oversample: 4 },
  'tube-set': { kind: 'triode', amount: 0.85, asymmetry: 0.8, bias: 0.85, headroomDb: 8, oversample: 8 },
  'tube-otl': { kind: 'triode', amount: 0.7, asymmetry: 0.5, bias: 0.5, headroomDb: 9, oversample: 8 },
  hybrid: { kind: 'triode', amount: 0.5, asymmetry: 0.55, bias: 0.6, headroomDb: 12, oversample: 4 },
  'transformer-coupled': { kind: 'transformer-core', amount: 0.6, coreKnee: 0.5, hysteresis: 0.25, headroomDb: 9, oversample: 4 },
};

/** Build a -3 dB bandwidth response from two corner frequencies. */
export function bandwidthResponse(lo: number, hi: number, order = 1): FRCurve {
  const pts: FRPoint[] = [];
  for (const f of [2, 5, 10, 20, 50, 100, 1000, 10000, 20000, 40000, 60000, 100000, 200000]) {
    const hp = -10 * order * Math.log10(1 + Math.pow(lo / f, 2));
    const lp = -10 * order * Math.log10(1 + Math.pow(f / hi, 2));
    pts.push([f, Number((hp + lp).toFixed(3))]);
  }
  return { kind: 'fr', points: pts, confidence: 'DERIVED', source: `Derived from quoted -3 dB bandwidth ${lo} Hz - ${hi} Hz` };
}

export function makeAmp(i: AmpSpecInput): AmpProfile {
  const id = slug(i.mfr, i.model, 'amp');
  const isTube = i.topology.startsWith('tube') || i.topology === 'transformer-coupled';
  const hasXfmr = i.outputTransformer ?? (i.topology === 'tube-set' || i.topology === 'tube-push-pull' || i.topology === 'transformer-coupled');
  const zOut =
    i.outputImpedanceOhms ??
    (i.dampingFactor ? 8 / i.dampingFactor : i.topology === 'tube-set' ? 2.2 : i.topology === 'tube-otl' ? 1.6 : isTube ? 1.1 : 0.04);

  const bw = i.bandwidth ?? (hasXfmr ? [12, 45000] : i.topology === 'class-d' ? [8, 45000] : [3, 120000]);

  const output: AmpOutputModel = {
    outputImpedanceOhms: zOut,
    feedbackDb: i.feedbackDb ?? (i.topology === 'tube-set' ? 0 : isTube ? 8 : i.topology === 'class-d' ? 26 : 30),
    outputTransformer: hasXfmr,
    transformerLfHz: hasXfmr ? (i.transformerLfHz ?? bw[0] * 1.4) : undefined,
    transformerHfHz: hasXfmr ? (i.transformerHfHz ?? bw[1] * 0.9) : undefined,
    dampingFactor: i.dampingFactor ?? Number((8 / zOut).toFixed(1)),
  };

  const dynamics: AmpDynamicsModel = {
    sagDb: i.topology === 'tube-set' ? 1.6 : isTube ? 1.1 : i.topology === 'class-d' ? 0.15 : 0.5,
    sagTimeMs: isTube ? 180 : 60,
    recoveryMs: isTube ? 420 : 140,
    thermalDb: i.topology === 'solid-state-a' ? 0.35 : 0.12,
    thermalTimeMs: 6000,
    slewRateVus: i.topology === 'class-d' ? 25 : isTube ? 18 : 60,
    headroomDb: i.topology === 'class-d' ? 0.8 : isTube ? 1.2 : 2.0,
    ...i.dynamics,
  };

  const clipping: AmpClippingModel = {
    kind: isTube ? 'soft' : i.topology === 'class-d' ? 'hard' : 'asymmetric',
    kneeDb: isTube ? 7 : i.topology === 'class-d' ? 0.4 : 2.5,
    stickiness: isTube ? 0.35 : 0.08,
    onsetDbFS: -1.0,
    ...i.clipping,
  };

  return {
    id,
    category: 'amp',
    manufacturer: i.mfr,
    model: i.model,
    price: i.price,
    version: i.version ?? 1,
    revised: '2026-08-22',
    confidence: i.confidence ?? 'ESTIMATED',
    provenanceNote:
      i.provenanceNote ??
      'Power, distortion and bandwidth from published manufacturer data; nonlinear and dynamic behaviour derived from those figures plus topology-typical harmonic structure.',
    notes: i.notes,
    tags: [...(i.tags ?? []), i.topology, i.form],
    year: i.year,
    countryOfOrigin: i.country,
    image: img(id, i.accent),
    topology: i.topology,
    form: i.form,
    devices: i.devices,
    power: i.power,
    gainDb: i.gainDb ?? (i.form === 'headphone' ? 14 : i.form === 'integrated' ? 36 : 29),
    response: i.response ?? bandwidthResponse(bw[0], bw[1], hasXfmr ? 1 : 1),
    phase: { kind: 'phase', derive: 'minimum-phase', confidence: 'DERIVED' },
    output,
    nonlinear: { ...AMP_NL_BY_TOPOLOGY[i.topology], confidence: 'MODELLED', ...i.nonlinear },
    harmonics: harmonicsFromThd(
      i.thdPercent,
      i.harmonicShape ?? AMP_SHAPE_BY_TOPOLOGY[i.topology],
      -6,
      'DERIVED',
      `Distributed from published THD ${i.thdPercent}%`,
    ),
    noise: noiseFrom(i.snrDb, isTube ? 'pink' : 'thermal', { channelCorrelation: isTube ? 0.25 : 0.05 }),
    channels: channels(i.crosstalkDb ?? (isTube ? -68 : -95), isTube ? 0.25 : 0.06),
    dynamics,
    clipping,
    balancedInput: i.balancedInput,
    electrostaticDrive: i.electrostaticDrive,
    estatBias: i.estatBias,
    thdPercent: i.thdPercent,
    snrDb: i.snrDb,
  };
}

// ---------------------------------------------------------------------------
// Speaker builder
// ---------------------------------------------------------------------------

export interface SpeakerSpecInput {
  mfr: string;
  model: string;
  price: PriceInfo;
  year?: number;
  country?: string;
  speakerType: SpeakerType;
  enclosure: EnclosureType;
  drivers: DriverTech[];
  wayCount?: number;
  driverCount?: number;
  active?: boolean;
  sensitivityDb: number;
  nominalOhms: number;
  minOhms?: number;
  /** [-3 dB, -6 dB] bass extension in Hz. */
  bass: [number, number?];
  /** Highest usable frequency, Hz. */
  hfExtension?: number;
  crossovers: Array<[number, number] | CrossoverSection>;
  /** Broad tonal shaping breakpoints on top of the derived baseline. */
  responseTilt?: FRPoint[];
  response?: FRCurve;
  impedance?: ImpedanceCurve;
  portTuningHz?: number;
  resonances?: Array<[number, number, number]>;
  coverageAngleDeg?: number;
  baffleStepHz?: number;
  recommendedWatts?: [number, number];
  dimensionsMm?: [number, number, number];
  massKg?: number;
  thdPercent?: number;
  harmonicShape?: HarmonicShape;
  roomCoupling?: SpeakerProfile['roomCoupling'];
  notes?: string;
  tags?: string[];
  confidence?: Confidence;
  provenanceNote?: string;
  accent?: string;
  version?: number;
}

/** Build a plausible impedance curve from nominal/min plus alignment. */
export function deriveImpedance(
  nominal: number,
  min: number,
  enclosure: EnclosureType,
  portHz: number | undefined,
  bassHz: number,
  crossovers: number[],
): ImpedanceCurve {
  const pts: ZPoint[] = [];
  const fb = portHz ?? bassHz * 0.95;
  const push = (f: number, m: number, p: number) => pts.push([f, Number(m.toFixed(2)), Number(p.toFixed(1))]);

  if (enclosure === 'ported' || enclosure === 'passive-radiator') {
    push(10, nominal * 0.95, -35);
    push(fb * 0.62, nominal * 3.4, 40);   // lower impedance peak
    push(fb, min * 1.15, 0);              // saddle at box tuning
    push(fb * 1.55, nominal * 3.0, -32);  // upper peak
    push(fb * 2.6, nominal * 1.25, -12);
  } else if (enclosure === 'dipole-panel' || enclosure === 'open-baffle') {
    push(10, nominal * 6, -60);
    push(bassHz, nominal * 2.2, -35);
    push(bassHz * 2.2, nominal * 1.2, -10);
  } else if (enclosure === 'horn-loaded') {
    push(10, nominal * 1.2, -20);
    push(bassHz * 0.9, nominal * 4.2, 25);
    push(bassHz * 1.8, min * 1.3, -8);
    push(bassHz * 3.5, nominal * 1.4, 6);
  } else {
    push(10, nominal * 1.1, -25);
    push(bassHz * 1.05, nominal * 3.8, 0); // single sealed resonance
    push(bassHz * 2.4, nominal * 1.3, -18);
  }

  push(200, Math.max(min, nominal * 0.85), -14);
  for (const xo of crossovers) {
    push(xo * 0.72, Math.max(min, nominal * 0.78), -18);
    push(xo, Math.max(min * 1.05, nominal * 0.95), 4);
    push(xo * 1.4, Math.max(min, nominal * 0.88), -8);
  }
  push(4000, nominal * 1.05, 8);
  push(10000, nominal * 1.35, 22);
  push(20000, nominal * 1.7, 30);

  pts.sort((a, b) => a[0] - b[0]);
  // remove duplicate frequencies
  const uniq: ZPoint[] = [];
  for (const p of pts) if (!uniq.length || p[0] > uniq[uniq.length - 1][0]) uniq.push(p);

  // For electrostatics the impedance falls with frequency (capacitive load).
  if (enclosure === 'dipole-panel') {
    return {
      kind: 'impedance',
      points: uniq.map(([f, m, ph]) =>
        [f, Number(Math.max(min * 0.9, m * Math.min(1, Math.pow(1000 / Math.max(f, 100), 0.62))).toFixed(2)), f > 2000 ? -62 : ph] as ZPoint,
      ),
      nominalOhms: nominal,
      minOhms: min,
      confidence: 'MODELLED',
      source: 'Capacitive panel load model from nominal/minimum impedance',
    };
  }

  return {
    kind: 'impedance',
    points: uniq,
    nominalOhms: nominal,
    minOhms: min,
    confidence: 'MODELLED',
    source: 'Alignment-derived impedance model from nominal/minimum impedance and box tuning',
  };
}

/** Baseline anechoic response: bass rolloff + HF extension + optional tilt. */
export function deriveResponse(
  bass3: number,
  bass6: number | undefined,
  hf: number,
  order: number,
  tilt?: FRPoint[],
): FRCurve {
  const f6 = bass6 ?? bass3 * 0.82;
  // Effective high-pass order implied by the f3/f6 ratio.
  const ratio = bass3 / f6;
  const impliedOrder = ratio > 1.35 ? 4 : ratio > 1.15 ? order : 2;
  const pts: FRPoint[] = [];
  const grid = [10, 16, 20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630,
    800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000, 10000, 12500, 16000, 20000, 25000, 40000];
  for (const f of grid) {
    const hp = -10 * impliedOrder * Math.log10(1 + Math.pow(bass3 / f, 2));
    const lp = -10 * 2 * Math.log10(1 + Math.pow(f / hf, 4));
    let v = hp + lp;
    if (tilt) {
      // Interpolate the author-supplied tonal shaping onto this point.
      v += evalTilt(tilt, f);
    }
    pts.push([f, Number(v.toFixed(2))]);
  }
  return {
    kind: 'fr',
    points: pts,
    interp: 'log-pchip',
    confidence: 'MODELLED',
    source: 'Alignment-derived baseline from quoted bass extension and HF limit, plus authored voicing offsets',
  };
}

function evalTilt(tilt: FRPoint[], f: number): number {
  if (!tilt.length) return 0;
  if (f <= tilt[0][0]) return tilt[0][1];
  if (f >= tilt[tilt.length - 1][0]) return tilt[tilt.length - 1][1];
  for (let i = 0; i < tilt.length - 1; i++) {
    if (f >= tilt[i][0] && f <= tilt[i + 1][0]) {
      const t = (Math.log(f) - Math.log(tilt[i][0])) / (Math.log(tilt[i + 1][0]) - Math.log(tilt[i][0]) || 1);
      return tilt[i][1] + t * (tilt[i + 1][1] - tilt[i][1]);
    }
  }
  return 0;
}

const SPEAKER_SHAPE: Partial<Record<SpeakerType, HarmonicShape>> = {
  horn: HARMONIC_SHAPES.horn,
  electrostatic: HARMONIC_SHAPES.electrostatic,
  'planar-magnetic': HARMONIC_SHAPES.planar,
  dipole: HARMONIC_SHAPES.electrostatic,
};

const SPEAKER_NL: Partial<Record<SpeakerType, NonlinearModel>> = {
  electrostatic: { kind: 'electrostatic', amount: 0.5, asymmetry: 0.05, bias: -0.6, headroomDb: 4, oversample: 2 },
  'planar-magnetic': { kind: 'planar', amount: 0.5, asymmetry: 0.25, bias: 0.2, headroomDb: 6, oversample: 2 },
  dipole: { kind: 'electrostatic', amount: 0.45, asymmetry: 0.1, bias: -0.4, headroomDb: 5, oversample: 2 },
  horn: { kind: 'dynamic-driver', amount: 0.6, asymmetry: 0.5, bias: 0.6, headroomDb: 6, oversample: 2 },
};

export function makeSpeaker(i: SpeakerSpecInput): SpeakerProfile {
  const id = slug(i.mfr, i.model, 'speaker');
  const xos: CrossoverSection[] = i.crossovers.map((c) =>
    Array.isArray(c)
      ? { freqHz: c[0], slopeDbOct: c[1], alignment: 'linkwitz-riley' as const }
      : c,
  );
  const min = i.minOhms ?? i.nominalOhms * 0.72;
  const hf = i.hfExtension ?? (i.drivers.some((d) => d === 'ribbon' || d === 'amt' || d === 'beryllium-dome' || d === 'diamond-dome') ? 40000 : 24000);
  const rolloffOrder: 2 | 4 = i.enclosure === 'ported' || i.enclosure === 'passive-radiator' ? 4 : 2;

  const cabinet: CabinetModel = {
    enclosure: i.enclosure,
    portTuningHz: i.enclosure === 'ported' || i.enclosure === 'passive-radiator' ? (i.portTuningHz ?? i.bass[0] * 0.95) : undefined,
    portCompression: i.enclosure === 'ported' ? 0.18 : undefined,
    portPipeHz: i.enclosure === 'ported' ? (i.portTuningHz ?? i.bass[0]) * 9.2 : undefined,
    resonances: i.resonances ?? [[Math.round(i.bass[0] * 4.6), -36, 14], [Math.round(i.bass[0] * 11), -42, 20]],
    qtc: i.enclosure === 'sealed' ? 0.68 : undefined,
    f3Hz: i.bass[0],
    f6Hz: i.bass[1],
    rolloffOrder,
  };

  const directivity: DirectivityModel = {
    coverageAngleDeg:
      i.coverageAngleDeg ??
      (i.speakerType === 'horn' ? 60
        : i.speakerType === 'electrostatic' || i.speakerType === 'dipole' ? 45
          : i.speakerType === 'omnidirectional' ? 340
            : i.drivers.includes('ribbon') || i.drivers.includes('amt') ? 100
              : 120),
    narrowingStartHz: i.speakerType === 'horn' ? 900 : 1400,
    narrowingSlopeDbOct: i.speakerType === 'omnidirectional' ? 0.3 : 1.1,
    patternOrder: i.speakerType === 'dipole' || i.speakerType === 'electrostatic' || i.enclosure === 'open-baffle' ? 1 : 0,
    baffleStepHz: i.baffleStepHz ?? (i.dimensionsMm ? Math.round(115000 / Math.max(120, i.dimensionsMm[1])) : 380),
    baffleStepDb: i.speakerType === 'dipole' || i.speakerType === 'electrostatic' ? 0 : -3.2,
    confidence: 'MODELLED',
  };

  const distortion: SpeakerDistortionModel = {
    harmonics: harmonicsFromThd(
      i.thdPercent ?? (i.speakerType === 'electrostatic' ? 0.12 : i.speakerType === 'horn' ? 0.45 : 0.35),
      i.harmonicShape ?? SPEAKER_SHAPE[i.speakerType] ?? HARMONIC_SHAPES.speaker,
      -12,
      'MODELLED',
      'Class-typical harmonic structure at ~90 dB SPL / 1 m',
    ),
    refSpl: 90,
    excursionKneeHz: i.bass[0] * 1.35,
    excursionRiseDbOct: i.speakerType === 'electrostatic' ? 14 : i.enclosure === 'ported' ? 11 : 7,
    compressionDb: i.speakerType === 'horn' ? 0.4 : i.active ? 0.8 : 1.4,
  };

  return {
    id,
    category: 'speaker',
    manufacturer: i.mfr,
    model: i.model,
    price: i.price,
    version: i.version ?? 1,
    revised: '2026-08-22',
    confidence: i.confidence ?? 'ESTIMATED',
    provenanceNote:
      i.provenanceNote ??
      'Sensitivity, impedance and bass extension from published manufacturer data; anechoic response, directivity and distortion are modelled from the stated alignment and driver complement.',
    notes: i.notes,
    tags: [...(i.tags ?? []), i.speakerType, i.enclosure, ...i.drivers],
    year: i.year,
    countryOfOrigin: i.country,
    image: img(id, i.accent),
    speakerType: i.speakerType,
    drivers: i.drivers,
    driverCount: i.driverCount,
    wayCount: i.wayCount ?? xos.length + 1,
    active: i.active,
    sensitivityDb: i.sensitivityDb,
    impedance: i.impedance ?? deriveImpedance(i.nominalOhms, min, i.enclosure, i.portTuningHz, i.bass[0], xos.map((x) => x.freqHz)),
    response: i.response ?? deriveResponse(i.bass[0], i.bass[1], hf, rolloffOrder, i.responseTilt),
    phase: { kind: 'phase', derive: 'minimum-phase', confidence: 'DERIVED' },
    crossover: xos,
    cabinet,
    directivity,
    distortion,
    nonlinear: { ...(SPEAKER_NL[i.speakerType] ?? { kind: 'dynamic-driver', amount: 0.45, asymmetry: 0.4, bias: 0.45, headroomDb: 8, oversample: 2 }), confidence: 'MODELLED' },
    recommendedWatts: i.recommendedWatts,
    dimensionsMm: i.dimensionsMm,
    massKg: i.massKg,
    roomCoupling: i.roomCoupling ?? (i.speakerType === 'dipole' || i.speakerType === 'electrostatic' ? 'high' : i.enclosure === 'sealed' ? 'low' : 'medium'),
  };
}

// ---------------------------------------------------------------------------
// Headphone builder
// ---------------------------------------------------------------------------

export interface HeadphoneSpecInput {
  mfr: string;
  model: string;
  price: PriceInfo;
  year?: number;
  country?: string;
  headphoneType: HeadphoneProfile['headphoneType'];
  fit: HeadphoneProfile['fit'];
  response: FRCurve;
  biasVolts?: number;
  capacitancePf?: number;
  impedanceOhms?: number;
  sensitivityDbVrms?: number;
  thdPercent?: number;
  requiresEnergiser?: boolean;
  estatBias?: HeadphoneProfile['estatBias'];
  sealSensitivityDb?: number;
  cupResonanceHz?: number;
  harmonicShape?: HarmonicShape;
  nonlinear?: Partial<NonlinearModel>;
  channels?: ChannelBehaviour;
  target?: HeadphoneProfile['target'];
  notes?: string;
  tags?: string[];
  confidence?: Confidence;
  provenanceNote?: string;
  accent?: string;
  suggestedCrossfeed?: HeadphoneProfile['suggestedCrossfeed'];
  version?: number;
}

export function makeHeadphone(i: HeadphoneSpecInput): HeadphoneProfile {
  const id = slug(i.mfr, i.model, 'hp');
  const shape =
    i.harmonicShape ??
    (i.headphoneType === 'electrostatic'
      ? HARMONIC_SHAPES.electrostatic
      : i.headphoneType === 'planar-magnetic'
        ? HARMONIC_SHAPES.planar
        : HARMONIC_SHAPES.speaker);
  const nlBase: NonlinearModel =
    i.headphoneType === 'electrostatic'
      ? { kind: 'electrostatic', amount: 0.45, asymmetry: 0.04, bias: -0.55, headroomDb: 4, oversample: 4 }
      : i.headphoneType === 'planar-magnetic'
        ? { kind: 'planar', amount: 0.45, asymmetry: 0.2, bias: 0.2, headroomDb: 6, oversample: 2 }
        : { kind: 'dynamic-driver', amount: 0.5, asymmetry: 0.45, bias: 0.5, headroomDb: 7, oversample: 2 };

  return {
    id,
    category: 'headphone',
    manufacturer: i.mfr,
    model: i.model,
    price: i.price,
    version: i.version ?? 1,
    revised: '2026-08-22',
    confidence: i.confidence ?? 'ESTIMATED',
    provenanceNote: i.provenanceNote,
    notes: i.notes,
    tags: [...(i.tags ?? []), i.headphoneType, i.fit],
    year: i.year,
    countryOfOrigin: i.country,
    image: img(id, i.accent),
    headphoneType: i.headphoneType,
    fit: i.fit,
    response: i.response,
    target: i.target,
    phase: { kind: 'phase', derive: 'minimum-phase', confidence: 'DERIVED' },
    biasVolts: i.biasVolts,
    capacitancePf: i.capacitancePf,
    impedanceOhms: i.impedanceOhms,
    sensitivityDbVrms: i.sensitivityDbVrms,
    distortion: {
      harmonics: harmonicsFromThd(i.thdPercent ?? 0.1, shape, -12, 'MODELLED', 'Class-typical structure at ~100 dB SPL'),
      refSpl: 100,
      excursionKneeHz: i.headphoneType === 'electrostatic' ? 45 : 30,
      excursionRiseDbOct: i.headphoneType === 'electrostatic' ? 12 : 8,
      compressionDb: 0.3,
    },
    nonlinear: { ...nlBase, confidence: 'MODELLED', ...i.nonlinear },
    channels: i.channels ?? channels(-40, 0.15, { crosstalkSlopeDbDec: 0 }),
    sealSensitivityDb: i.sealSensitivityDb ?? 3,
    cupResonanceHz: i.cupResonanceHz,
    requiresEnergiser: i.requiresEnergiser,
    estatBias: i.estatBias,
    suggestedCrossfeed: i.suggestedCrossfeed ?? 'low',
  };
}

export { frCurve, impedanceCurve };
